import { Params } from '@angular/router'
import { HttpRequestOptions } from '../services/http/http.service'
import { NavigateType } from '../services/router/router.service'
import { AttachmentTypeLogEvent } from './log/attachment-type-log'
import { EgenTetoQuizLogEvent } from './log/egen-teto-quiz-log'
import { HspQuizLogEvent } from './log/hsp-quiz-log'
import { SignalLogEvent } from './log/signal-log'
import { ThinkingChairLogEvent } from './log/thinking-chair-log'
import { AppUpdateStatus } from './main'

export type ScreenName =
  | 'home'
  | 'ask_post_detail'
  | 'mbti_host_quiz_report'
  | 'friend_home'
  | 'friend_list'
  | 'menu'
  | 'login'
  | 'profile'
  | 'fit_report_intro'
  | 'mbti_quiz_create'
  | 'mbti_reply_create'
  | 'fw_quiz_create'
  | 'fw_reply_create'
  | 'cabinet_login'
  | 'survey_result'
  | 'ideal_type_quiz_create'
  | 'ideal_type_reply_create'
  | 'daily_log_quiz_landing'

export enum LoginType {
  CLICK_SHOW_SURVEY_RESULT = 'click_show_survey_result',
  CLICK_SURVEY_ITEM = 'click_survey_item',
  CLICK_ASK_CREATE = 'click_ask_create',
  CLICK_ASK = 'click_ask',
  CLICK_ASK_MORE = 'click_ask_more',
  HEADER = 'header',
  AUTH_GUARD = 'auth_guard',
  CLICK_CREATE_QUIZ = 'click_create_quiz',
  LOGOUT = 'logout',
  CLICK_START_THINKING_CHAIR = 'click_start_thinking_chair',
  UNAUTHORIZED = 'unauthorized',
  WITHDRAWN_USER = 'withdrawn_user',
}

export type EventLoggingParams = Params & {
  screenName?: ScreenName
}
export type LogEventKey = { ga?: string; amp?: string }
export const LogEvent = {
  ...AttachmentTypeLogEvent,
  ...SignalLogEvent,
  ...ThinkingChairLogEvent,
  ...HspQuizLogEvent,
  ...EgenTetoQuizLogEvent,
  LOG_SESSION_START: { ga: 'session_start' },
  APP_OPENED: { ga: 'open_app', amp: 'App - Opened' },
  HEADER_LOGO_CLICKED: { ga: 'header_logo_click', amp: 'Header - Logo Clicked' },
  HEADER_DRAWER_CLICKED: { ga: 'header_drawer_click', amp: 'Header - Drawer Clicked' },

  // # **친구**
  FRIENDS_MORE: { ga: 'friends_more', amp: 'Friends - More Button Clicked' }, // 친구 목록 화면에서 “더 보기” 클릭
  FRIENDS_ENTER_PROFILE: { ga: 'friends_enter_profile', amp: 'Friends - Enter Profile' }, // 친구 목록 화면에서 프로필 화면으로 진입
  FRIENDS_START_QUIZ: { ga: 'friends_start_quiz', amp: 'Friends - Start Quiz' }, // 친구 목록 화면에서 `성격 유형 확인하기` 버튼 클릭
  FRIENDS_TOGGLE_LIST: { ga: 'friends_toggle_list', amp: 'Friends - Replied Friends List Toggle' }, // 친구 목록 화면에서 `성격 유형을 확인한 친구` 목록 토글

  // # 호불호 월드컵

  // **퀴즈 생성**
  MBTI_GENERATOR_START: { ga: 'mbti_generator_start' },
  MBTI_GENERATOR_SUBMIT: { ga: 'mbti_generator_submit' },
  MBTI_GENERATOR_EDIT: { ga: 'mbti_generator_edit' },
  MBTI_GENERATOR_BACK: { ga: 'mbti_generator_back' },
  MBTI_GENERATOR_NEXT: { ga: 'mbti_generator_next' },
  FW_GENERATOR_START: { ga: 'fw_generator_start' }, // 호스트가 호불호 월드컵 주제 선택
  FW_GENERATOR_SUBMIT: { ga: 'fw_generator_submit' }, // 호스트가 거의 완성되었습니다! 화면에서 `호불호 월드컵 완성하기` 버튼 클릭

  // **퀴즈 공유**

  FW_CHEMI_INVITE_KAKAO: { ga: 'fw_chemi_invite_kakao', amp: 'FW Chemi - Invite Kakao Clicked' }, // 호스트가 호불호 월드컵 결과 화면에서 `카카오톡으로 전송` 버튼 클릭
  FW_QUIZ_SHARE_KAKAO: { ga: 'fw_quiz_share_kakao', amp: 'FW - Host Quiz Detail - Share Kakao Clicked' }, // 호스트가 호스트 퀴즈 리포트 디테일에서 `카카오톡으로 공유` 버튼 클릭
  FW_QUIZ_SHARE_COPY: { ga: 'fw_quiz_share_copy', amp: 'FW - Host Quiz Detail - Share URL Clicked' }, // 호스트가 호스트 퀴즈 리포트 디테일에서 url `복사` 버튼 클릭
  FW_QUIZ_SHARE_WEB_API: { ga: 'fw_quiz_share_web_api', amp: 'FW - Host Quiz Detail - Share Webshare Clicked' }, // 호스트가 호스트 퀴즈 리포트 디테일에서 share_api 호출
  MBTI_QUIZ_SHARE_KAKAO: { ga: 'mbti_quiz_share_kakao', amp: 'Mbti Quiz - Host Quiz Detail - Share Kakao Clicked' },
  MBTI_QUIZ_SHARE_QR_CODE: { ga: 'mbti_quiz_share_qr_code', amp: 'Mbti Quiz - Host Quiz Detail - QR Code Clicked' },
  MBTI_QUIZ_SHARE_COPY: { ga: 'mbti_quiz_share_copy', amp: 'Mbti Quiz - Host Quiz Detail - Share URL Clicked' },
  MBTI_QUIZ_SHARE_WEB_API: {
    ga: 'mbti_quiz_share_web_api',
    amp: 'Mbti Quiz - Host Quiz Detail - Share Webshare clicked',
  },
  SNS_SHARE: { ga: 'sns_share', amp: 'SNS Share Clicked' }, // 소셜 공유 버튼 클릭

  // **퀴즈 응답**
  MBTI_QUIZ_BACK: { ga: 'mbti_quiz_back' },
  MBTI_QUIZ_FINISH: { ga: 'mbti_quiz_finish' },
  MBTI_QUIZ_NEXT: { ga: 'mbti_quiz_next' },
  MBTI_ANSWER_GUEST_MAKEOWN: { ga: 'mbti_answer_guest_makeown' },
  FW_QUIZ_FINISH: { ga: 'fw_quiz_finish' }, // 게스트가 호불호 월드컵 결승전에서 `다음` 버튼 클릭
  FW_ANSWER_GUEST_MAKEOWN: { ga: 'fw_answer_guest_makeown', amp: 'FW - Guest Reply Detail - Make Own Clicked' }, // 게스트가 게스트 퀴즈 리포트 디테일에서 `내 호불호 월드컵 퀴즈 만들기` 버튼 클릭

  // 퀴즈 리포트
  REPORT_ANSWER: { ga: 'report_answer' }, // mbti host quiz report에서 응답 상세로 이동시
  BANNER_SIGNUP: { ga: 'banner_signup' }, // mbti 퀴즈 생성 결과의 배너에서 회원가입 클릭시
  BANNER_SURVEY: { ga: 'banner_survey' }, // mbti 퀴즈 생성 결과의 배너에서 설문조사 클릭시
  ANSWER_HOST_BACK: { ga: 'answer_host_back' }, // 퀴즈 응답결과에서 답변 비교의 '이전' 버튼 클릭시
  ANSWER_GUEST_BACK: { ga: 'answer_guest_back' },
  ANSWER_HOST_NEXT: { ga: 'answer_host_next' }, // 퀴즈 응답결과에서 답변 비교의 '다음' 버튼 클릭시
  ANSWER_GUEST_NEXT: { ga: 'answer_guest_next' },

  // 호불호 퀴즈 리포트
  FW_GUEST_REPLY_DETAIL_VIEWED: { ga: 'fw_quiz_guest_reply_detail_visit', amp: 'FW - Guest Reply Detail - Viewed' }, // [호불호 월드컵 > 게스트 응답 디테일] 게스트 응답 상세로 이동
  FW_GUEST_REPLY_DETAIL_NEXT_CLICKED: {
    ga: 'fw_guest_reply_detail_next',
    amp: 'FW - Guest Reply Detail - Next Clicked',
  }, // [호불호 월드컵 > 게스트 응답 디테일] 다음 버튼 클릭
  FW_GUEST_REPLY_DETAIL_PREV_CLICKED: {
    ga: 'fw_guest_reply_detail_prev',
    amp: 'FW - Guest Reply Detail - Prev Clicked',
  }, // [호불호 월드컵 > 게스트 응답 디테일] 이전 버튼 클릭
  FW_HOST_REPLY_DETAIL_VIEWED: { ga: 'fw_quiz_guest_reply_detail_visit', amp: 'FW - Host Reply Detail - Viewed' }, // [호불호 월드컵 > 호스트 응답 디테일] 게스트 응답 상세로 이동
  FW_HOST_REPLY_DETAIL_NEXT_CLICKED: { ga: 'fw_host_reply_detail_next', amp: 'FW - Host Reply Detail - Next Clicked' }, // [호불호 월드컵 > 호스트 응답 디테일] 다음 버튼 클릭
  FW_HOST_REPLY_DETAIL_PREV_CLICKED: { ga: 'fw_host_reply_detail_prev', amp: 'FW - Host Reply Detail - Prev Clicked' }, // [호불호 월드컵 > 호스트 응답 디테일] 이전 버튼 클릭
  FW_HOST_QUIZ_DETAIL_VIEWED: { ga: 'fw_quiz_host_quiz_detail_visit', amp: 'FW - Host Quiz Detail - Viewed' }, // [호불호 월드컵 > 호스트 퀴즈 디테일] 게스트 응답 상세로 이동

  // # 케미
  CHEMI_START: { ga: 'chemi_start' },
  CHEMI_NEXT: { ga: 'chemi_next' },
  CHEMI_FINISH: { ga: 'chemi_finish' },
  CHEMI_FIND_FRIENDS: { ga: 'chemi_find_friends' },
  CHEMI_TABLE_OF_CONTENT: { ga: 'chemi_table_of_content' }, // 케미 결과 페이지에서 스크롤링 드랍다운 클릭시

  // # 알림

  NOTIFICATION_CLICK_NEW_QUIZ: { ga: 'notification_click_new_quiz' }, // 알림 화면에서 새 성격 유형 퀴즈 알림 클릭
  NOTIFICATION_CLICK_NEW_FW: { ga: 'notification_click_new_fw' }, // 알림 화면에서 새 호불호 월드컵 퀴즈 알림 클릭
  NOTIFICATION_CLICK_NEW_QUIZ_RESPONSE: { ga: 'notification_click_new_quiz_response' }, // 알림 화면에서 새 성격 유형 응답 알림 클릭
  NOTIFICATION_CLICK_NEW_FW_RESPONSE: { ga: 'notification_click_new_fw_response' }, // 알림 화면에서 새 호불호 월드컵 퀴즈 알림 클릭
  NOTIFICATION_CLICK_ETC: { ga: 'notification_click_etc' }, // 알림 화면에서 정의되지 않은 기타 알림 클릭

  // # 메인
  HOME_FEED_CLICK: { ga: 'home_feed_clicked', amp: 'Home - Feed Clicked' },
  HOME_MBTI_QUIZ: { ga: 'home_mbti_quiz_click', amp: 'Home - Mbti Quiz Clicked' }, // 유저가 메인에서 `mbti` 배너 클릭
  HOME_DAILY_LOG: { ga: 'home_daily_log_click', amp: 'Home - Daily Log Clicked' }, // 유저가 메인에서 `일상 로그` 배너 클릭
  HOME_FW_QUIZ: { ga: 'home_fw_quiz_click', amp: 'Home - FW Quiz Clicked' }, // 유저가 메인에서 `호불호 월드컵` 배너 클릭
  HOME_MBTI_TEST: { ga: 'home_mbti_test_click', amp: 'Home - Mbti Test Clicked' }, // [홈] ‘무료 성격 유형 검사' 버튼 클릭

  // # 메뉴

  MENU_CLICK_MAIN: { ga: 'menu_click_main' }, // (웹 전용) 유저가 메뉴 화면에서 `메인` 버튼 클릭
  MENU_CLICK_MY: { ga: 'menu_click_my' }, // (웹 전용) 유저가 메뉴 화면에서 `나의 타입스` 버튼 클릭
  MENU_CLICK_QUIZ: { ga: 'menu_click_quiz' }, // 유저가 메뉴 화면에서 `성격 유형 퀴즈` 버튼 클릭
  MENU_CLICK_FW: { ga: 'menu_click_fw' }, // 유저가 메뉴 화면에서 `호불호 월드컵` 버튼 클릭
  MENU_CLICK_FRIENDS: { ga: 'menu_click_friends' }, // 유저가 메뉴 화면에서 `친구` 버튼 클릭
  MENU_CLICK_CHEMI: { ga: 'menu_click_chemi' }, // 유저가 메뉴 화면에서 `케미 분석` 버튼 클릭
  MENU_CLICK_SETTING: { ga: 'menu_click_setting' },

  MENU_SERVICE_ITEM_CLICKED: { ga: 'click_item', amp: 'Menu - Service Item Clicked' },
  MENU_SIMPLE_ITEM_CLICKED: { ga: 'click_item', amp: 'Menu - Simple Item Clicked' },

  DRAWER_SERVICE_ITEM_CLICKED: { ga: 'click_item', amp: 'DRAWER - Service Item Clicked' },
  DRAWER_SIMPLE_ITEM_CLICKED: { ga: 'click_item', amp: 'DRAWER - Simple Item Clicked' },

  // # gnb tab
  GNB_HOME: { ga: 'gnb_home' }, // gnb에서 홈 버튼 클릭
  GNB_MENU: { ga: 'gnb_menu' }, // gnb에서 메뉴 버튼 클릭
  GNB_FRIENDS: { ga: 'gnb_friends' }, // gnb에서 친구 버튼 클릭
  GNB_PROFILE: { ga: 'gnb_profile' },

  // # login
  ENTER_LOGIN: { ga: 'enter_login' },

  // kakao
  KAKAO_LOGIN: { ga: 'kakao_login' },

  // ask
  ASK_PREVIEW_IMPRESSION: { ga: 'impression', amp: 'Quiz Result - Ask Preview Impression' },
  ASK_ALL_COMMENT_CLICKED: { ga: 'click_all_comment', amp: 'Ask Detail - See All Comment Clicked' },

  // survey
  SURVEY_RESULT_VIEWED: { ga: 'show_dialog', amp: 'Survey Result - Viewed' },
  HOME_SURVEY_ANSWER_CLICKED: { ga: 'click_survey_answer', amp: 'Home - Survey Answer Clicked' },
  HOME_SURVEY_ANSWER_POSTED: { ga: 'click_survey_answer', amp: 'Home - Survey Answer Posted' },
  SURVEY_RESULT_SURVEY_ANSWER_POSTED: { ga: 'click_survey_answer', amp: 'Survey Result - Survey Answer Posted' },
  HOME_SURVEY_ANSWER_UPDATED: { ga: 'click_survey_answer', amp: 'Home - Survey Answer Updated' },
  SURVEY_RESULT_SURVEY_ANWER_UPDATED: { ga: 'click_survey_answer', amp: 'Survey Result - Survey Answer Updated' },
  HOME_SURVEY_IMPRESSION: { ga: 'impression', amp: 'Home - Survey Impression' },
  SURVEY_RESULT_DISMISS: { ga: 'dismiss_dialog', amp: 'Survey Result - Dismiss' },
  HOME_SURVEY_RESULT_CLICK: { ga: 'home_survey_result_click', amp: 'Home - Survey Result Clicked' },

  HOME_SURVEY_COMMENT_WRITE_CLICKED: { ga: 'click_comment_write', amp: 'Main - Survey Comment Write Clicked' }, // [메인 - 서베이] ‘댓글 남기기’ 버튼 클릭
  HOME_SURVEY_SHOW_ALL_COMMENTS: { ga: 'click_see_all', amp: 'Main - Survey See all Comments Clicked' }, // [메인 - 서베이] ‘댓글 모두 보기’ 버튼 클릭
  SURVEY_RESULT_COMMENTS_DELETED: { ga: 'comment_delete', amp: 'Survey Result - Comment Deleted' }, // [서베이 결과모달] 댓글 삭제
  SURVEY_RESULT_COMMENTS_REPORTED: { ga: 'comment_report', amp: 'Survey Result - Comment Reported' }, // [서베이 결과모달] 댓글 신고
  SURVEY_RESULT_COMMENTS_IMPRESSION: { ga: 'impression', amp: 'Survey Result - Comments Impression' }, // [서베이 결과모달] 댓글 영역 노출
  SURVEY_RESULT_COMMENT_SUBMIT_CLICKED: { ga: 'click_comment_write', amp: 'Suvery Result - Comment Submit Clicked' }, // [서베이 결과모달] 댓글 입력 필드 옆 ‘게시’를 누름
  SURVEY_RESULT_KEYBOARD_OPENED: { ga: 'keyboard_opened', amp: 'Suvery Result - Comment Keyboard Opened' }, // [서베이 결과모달] 댓글 작성을 위한 키보드 열기
  SURVEY_RESULT_COMMENT_LIKE_SET_CLICKED: { ga: 'click_comment_like', amp: 'Suvery Result - Comment Like Set Clicked' }, // [서베이 결과모달] 댓글의 ‘라이크’ 눌러서 지정함
  SURVEY_RESULT_COMMENT_LIKE_UNSET_CLICKED: {
    ga: 'click_comment_like',
    amp: 'Suvery Result - Comment Like Unset Clicked',
  }, // [서베이 결과모달] 댓글의 ‘라이크’ 눌러서 해제함
  SURVEY_RESULT_SUMMARY_SEE_ALL_CLICKED: { ga: 'click_see_all', amp: 'Survey Result - Summary See All Clicked' }, // [서베이 결과모달] 모든유형보기(…) 버튼 클릭
  SURVEY_RESULT_SUMMARY_SCROLLED: { ga: 'scroll_summary', amp: 'Survey Result - Summary Scrolled' }, // [서베이 결과모달] 유형별 결과 좌우 스크롤

  //exp-041
  SURVEY_RESULT_CLICK_SHOW_ALL_BUTTON: {
    ga: 'survey_click_all_result_button',
    amp: 'Survey Result - Click Show All Button',
  }, //[서베이 결과] 모든 유형의 답변 보기 버튼
  SURVEY_RESULT_HOT_SURVEY_CLICKED: { ga: 'survey_hot_survey_clicked', amp: 'Survey Result - Hot Survey Clicked' }, //[서베이 결과] 모든 유형의 답변 보기 버튼
  SURVEY_RESULT_INSTALL_APP_VIEWED: { ga: 'survey_insall_app_viewed', amp: 'Survey Result - Install App Viewed' },
  SURVEY_RESULT_LOGIN_MODAL_VIEWED: { ga: 'survey_login_modal_viewed', amp: 'Survey Result - Login Modal Viewed' },
  SURVEY_RESULT_ALL_SURVEY_BUTTON_CLICKED: {
    ga: 'click_more_survey',
    amp: 'Survey Result - All Survey Button Clicked',
  },
  SURVEY_RESULT_SHARE_BUTTON_CLICKED: { ga: 'survey_click_share_button', amp: 'Survey Result - Share Button Clicked' },
  SURVEY_CLICK_COMMENT: { ga: 'survey_click_comment', amp: 'Survey - Click Comment ' },
  SURVEY_RESULT_IMPRESSION: { ga: 'survey_result_impression', amp: 'Survey Result - Impression' },
  SURVEY_RESULT_CLICK_RESULT_BUTTON: { ga: 'survey_click_result_button', amp: 'Survey - Click Result Button' },
  SURVEY_RESULT_CLICK_ANSWER: { ga: 'survey_click_answer', amp: 'Survey Result - Click Answer' },
  SURVEY_RESULT_UPDATE_ANSWER: { ga: 'survey_update_answer', amp: 'Survey Result - Update Answer' },

  // 기타
  PROFILE_SHARE_COPY: { ga: 'profile_share_copy', amp: 'Profile - Share Copy Clicked' },
  PROFILE_UPDATE_SUBMIT: { ga: 'profile_update_submit', amp: 'Profile - Update Submit' },
  FW_SUBSCRIPTION_ON: { ga: 'fw_subscription_on', amp: 'FW - Subscribed' }, // 호불호 월드컵 리스트 최하단 > 주제 추가 알림 버튼 클릭 시

  QUIZ_RESULT_MORE_POST_CLICKED: { ga: 'click_more', amp: 'Quiz Result - More Post Clicked' },
  HOME_NOTIFICATION_CLICKED: { amp: 'Home - Notification Clicked' },
  LOGIN_TERMS_GUIDE_DIALOG_VIEWED: { ga: 'show_dialog', amp: 'Login - Terms Guide Dialog Viewed' },
  LOGIN_TERMS_GUIDE_CONFIRM_CLICKED: { ga: 'click_confirm', amp: 'Login - Terms Guide Confirm Clicked' },
  ASK_LOCATION_INFO_DIALOG_VIEWED: { ga: 'show_dialog', amp: 'Ask Post Detail - Ask Location Info Dialog Viewed' },
  ASK_LOCATION_INFO_DIALOG_DISMISSED: {
    ga: 'dismiss_dialog',
    amp: 'Ask Post Detail - Ask Location Info Dialog Dismissed',
  },
  FRIEND_HOME_VIEWED: { ga: 'visit_friend_home', amp: 'Friend Home - Viewed' },
  FRIEND_LIST_VIEWED: { ga: 'visit_friend_list', amp: 'Friend List - Viewed' },
  LOGIN_VIEWED: { ga: 'visit_login', amp: 'Login - Viewed' },
  KAKAO_LOGIN_CLICKED: { ga: 'click_login', amp: 'Login - Kakao Login Clicked' },
  HOME_VIEWED: { ga: 'visit_home', amp: 'Home - Viewed' },
  MENU_VIEWED: { ga: 'visit_menu', amp: 'Menu - Viewed' },
  PROFILE_VIEWED: { ga: 'visit_profile', amp: 'Profile - Viewed' },
  ASK_PREVIEW_EXPAND: { ga: 'section_toggle', amp: 'Quiz Result - Ask Preview Expand' },
  ASK_PREVIEW_COLLAPSE: { ga: 'section_toggle', amp: 'Quiz Result - Ask Preview Collapse' },

  SIGN_UP_SUCCESS: { ga: 'sign_up', amp: 'Login - Sign Up Success' },
  SIGN_IN_SUCCESS: { ga: 'sign_in', amp: 'Login - Sign In Success' },
  LOGIN_FAILED: { ga: 'login_failed', amp: 'Login - Login Failed' },

  HOME_ASK_IMPRESSION: { ga: 'impression', amp: 'Home - Ask Impression' },
  HOME_ASK_NEW_POST_CLICKED: { ga: 'click_ask_new_post', amp: 'Home - Ask New Post Clicked' },
  HOME_ASK_POST_ITEM_CLICKED: { ga: 'click_ask_post_detail', amp: 'Home - Ask Post Item Clicked' },
  HOME_ASK_MORE_POST_CLICKED: { ga: 'click_more', amp: 'Home - Ask More Post Clicked' },
  ASK_POST_DETAIL_VIEWED: { ga: 'visit_ask_detail', amp: 'Ask Post Detail - Viewed' },
  ASK_POST_DETAIL_NEW_POST_CLICKED: { ga: 'click_ask_new_post', amp: 'Ask Post Detail - New Post Clicked' },
  ASK_POST_DETAIL_POST_LIKE_SET_CLICKED: { ga: 'click_post_like', amp: 'Ask Post Detail - Post Like Set Clicked' },
  ASK_POST_DETAIL_POST_LIKE_UNSET_CLICKED: { ga: 'click_post_like', amp: 'Ask Post Detail - Post Like Unset Clicked' },
  ASK_POST_DETAIL_COMMENT_LIKE_SET_CLICKED: {
    ga: 'click_comment_like',
    amp: 'Ask Post Detail - Comment Like Set Clicked',
  },
  ASK_POST_DETAIL_COMMENT_LIKE_UNSET_CLICKED: {
    ga: 'click_comment_like',
    amp: 'Ask Post Detail - Comment Like Unset Clicked',
  },
  ASK_POST_DETAIL_POST_REPORT_DIALOG_VIEWED: { ga: 'show_dialog', amp: 'Ask Post Detail - Post Report Dialog Viewed' },
  ASK_POST_DETAIL_POST_REPORT_CLICKED: { ga: 'click_submit', amp: 'Ask Post Detail - Post Report Clicked' },
  ASK_POST_DETAIL_COMMENT_REPORT_DIALOG_VIEWED: {
    ga: 'show_dialog',
    amp: 'Ask Post Detail - Comment Report Dialog Viewed',
  },
  ASK_POST_DETAIL_COMMENT_REPORT_CLICKED: { ga: 'click_submit', amp: 'Ask Post Detail - Comment Report Clicked' },
  ASK_POST_DETAIL_POST_DELETE_DIALOG_VIEWED: { ga: 'show_dialog', amp: 'Ask Post Detail - Post Delete Dialog Viewed' },
  ASK_POST_DETAIL_POST_DELETE_CLICKED: { ga: 'click_confirm', amp: 'Ask Post Detail - Post Delete Clicked' },
  ASK_POST_DETAIL_COMMENT_DELETE_DIALOG_VIEWED: {
    ga: 'show_dialog',
    amp: 'Ask Post Detail - Comment Delete Dialog Viewed',
  },
  ASK_POST_DETAIL_COMMENT_DELETE_CLICKED: { ga: 'click_confirm', amp: 'Ask Post Detail - Comment Delete Clicked' },
  ASK_POST_DETAIL_COMMENT_SUBMIT_CLICKED: { ga: 'click_submit', amp: 'Ask Post Detail - Comment Submit Clicked' },
  ASK_POST_DETAIL_NAVIGATION_BACK_PERFORMED: {
    ga: 'navigation_back_performed',
    amp: 'Ask Post Detail - Navigation Back Performed',
  },
  ASK_POST_DETAIL_SCROLL_ENDED: { ga: 'scroll_ended', amp: 'Ask Post Detail - Scroll Ended' },
  ASK_NEW_POST_MBTI_SELECT_VIEWED: { ga: 'show_dialog', amp: 'Ask New Post - Mbti Select Viewed' },
  ASK_NEW_POST_MBTI_PICKER_OPENED: { ga: 'open_option_list', amp: 'Ask New Post - Mbti Picker Opened' },
  ASK_NEW_POST_MBTI_SELECTED: { ga: 'select_option', amp: 'Ask New Post - Mbti Selected' },
  ASK_NEW_POST_MBTI_SELECT_DISMISSED: { ga: 'dismiss_dialog', amp: 'Ask New Post - Mbti Select Dismissed' },
  ASK_NEW_POST_CONTENT_INPUT_VIEWED: { ga: 'show_dialog', amp: 'Ask New Post - Content Input Viewed' },
  ASK_NEW_POST_POST_SUBMIT_CLICKED: { ga: 'click_submit', amp: 'Ask New Post - Post Submit Clicked' },
  ASK_NEW_POST_CONTENT_INPUT_DISMISSED: { ga: 'dismiss_dialog', amp: 'Ask New Post - Content Input Dismissed' },

  PROFILE_SETTINGS_CLCICKED: { ga: 'click_setting', amp: 'Profile - Settings Clicked' },
  PROFILE_LINK_SHARE_CLICKED: { ga: 'click_share', amp: 'Profile - Link Share Clicked' }, // [프로필 탭] 상단에 프로필 링크 공유 공유 아이콘 누름
  PROFILE_LINK_COPY_CLICKED: { amp: 'Profile - Link Copy Clicked' }, // [프로필 탭] ‘프로필 링크 복사’ 누름
  PROFILE_PUBLIC_QUIZ_TAB_CLICKED: { amp: 'Profile - Public Quiz Tab Clicked' }, // [프로필 탭] 공개 퀴즈 내역 탭 누름
  PROFILE_PRIVATE_QUIZ_TAB_CLICKED: { amp: 'Profile - Private Quiz Tab Clicked' }, // [프로필 탭] 비공개 퀴즈 내역 탭 누름
  PROFILE_ANSWER_TAB_CLICKED: { amp: 'Profile - Answer Tab Clicked' }, // [프로필 탭] 응답 내역 탭 누름
  PROFILE_ANSWER_ITEM_CLICKED: { amp: 'Profile - Answer Item Clicked' }, // [프로필 탭] 응답 내역 목록 중 항목을 누름
  PROFILE_PRIVATE_QUIZ_ITEM_CLICKED: { amp: 'Profile - Private Quiz Item Clicked' }, // [프로필 탭] 비공개 퀴즈 내역 목록 중 항목을 누름
  PROFILE_PUBLIC_QUIZ_ITEM_CLICKED: { amp: 'Profile - Public Quiz Item Clicked' }, // [프로필 탭] 공개 퀴즈 내역 목록 중 항목을 누름
  // 친구 프로필 진입
  FRIEND_PROFILE_VIEWED: { ga: 'visit_friend_profile', amp: 'Friend Profile - Viewed' },
  // 프로필 - 로그 퀴즈 item 노출
  PROFILE_LOG_QUIZ_LIST_VIEWED: {
    amp: 'Profile - Log Quiz List Viewed',
    ga: 'profile_log_quiz_list_viewed',
  },
  // 프로필 - 로그 퀴즈 item 클릭
  PROFILE_LOG_QUIZ_LIST_CLICKED: {
    amp: 'Profile - Log Quiz List Clicked',
    ga: 'profile_log_quiz_list_clicked',
  },
  // 남프로필 - 로그 퀴즈 item 클릭
  OTHERS_PROFILE_LOG_QUIZ_LIST_CLICKED: {
    amp: 'Others Profile - Log Quiz List Clicked',
    ga: 'others_profile_log_quiz_list_clicked',
  },
  // 남프로필 - 로그 퀴즈 item 노출
  OTHERS_PROFILE_LOG_QUIZ_LIST_VIEWED: {
    amp: 'Others Profile - Log Quiz List Viewed',
    ga: 'others_profile_log_quiz_list_viewed',
  },

  FIT_REPORT_INTRO_VIEWED: { amp: 'Fit Report Intro - Viewed' }, // [케미 분석 생성 Flow] 첫 화면 진입함 (’우리 유형, 케미는 어떨까?’가 있는 화면)
  FIT_REPORT_CREATE_SUCCESS: { amp: 'Fit Report - Create Success' }, // [케미 분석 생성 Flow] 생성 완료

  FW_QUIZ_CREATE_NAME_ENTRY_VIEWED: {
    ga: 'fw_quiz_create_name_entry_visit',
    amp: 'FW Quiz Create - Name Entry Viewed',
  }, // 프로필 선택 / 이름 입력 화면 진입
  FW_QUIZ_CREATE_LENGTH_VIEWED: { ga: 'fw_quiz_create_length_visit', amp: 'FW Quiz Create - Length Viewed' }, // 문항 수 선택 화면 진입
  FW_QUIZ_CREATE_QUESTIONNAIRE_VIEWED: {
    ga: 'fw_quiz_create_questionnaire_visit',
    amp: 'FW Quiz Create - Questionnaire Viewed',
  }, // 질문지 화면 진입
  FW_QUIZ_CREATE_SUBMIT: { ga: 'fw_quiz_create_submit', amp: 'FW Quiz Create - Submit' },
  FW_REPLY_CREATE_NAME_ENTRY_VIEWED: {
    ga: 'fw_reply_create_name_entry_visit',
    amp: 'FW Reply Create - Name Entry Viewed',
  }, // 프로필 선택 / 이름 입력 화면 진입
  FW_REPLY_CREATE_QUESTIONNAIRE_VIEWED: {
    ga: 'fw_reply_create_questionnaire_visit',
    amp: 'FW Reply Create - Questionnaire Viewed',
  }, // 질문지 화면 진입
  FW_REPLY_CREATE_SUBMIT: { ga: 'fw_reply_create_submit', amp: 'FW Reply Create - Submit' }, // 결승전에서 항목 선택
  MBTI_QUIZ_CREATE_LANDING_VIEWED: {
    ga: 'mbti_quiz_create_landing_visit',
    amp: 'Mbti Quiz Create - Landing Viewed',
  }, // 성격 유형 퀴즈 생성 랜딩 페이지 진입
  MBTI_QUIZ_CREATE_LANDING_START_CLICKED: {
    ga: 'mbti_quiz_create_landing_start',
    amp: 'Mbti Quiz Create - Landing Start Clicked',
  }, // 성격 유형 퀴즈 생성 랜딩 페이지에서 시작하기 버튼 클릭
  MBTI_QUIZ_CREATE_NAME_ENTRY_VIEWED: {
    ga: 'mbti_quiz_create_name_entry_visit',
    amp: 'Mbti Quiz Create - Name Entry Viewed',
  }, // 프로필 선택 / 이름 입력 화면 진입
  MBTI_QUIZ_CREATE_QUESTIONNAIRE_VIEWED: {
    ga: 'mbti_quiz_create_questionnaire_visit',
    amp: 'Mbti Quiz Create - Questionnaire Viewed',
  }, // 질문지 화면 진입
  MBTI_QUIZ_CREATE_SUBMIT: { ga: 'mbti_quiz_create_submit', amp: 'Mbti Quiz Create - Submit' }, // 마지막 ‘다음’버튼 클릭
  MBTI_REPLY_CREATE_NAME_ENTRY_VIEWED: {
    ga: 'mbti_reply_create_name_entry_visit',
    amp: 'Mbti Reply Create - Name Entry Viewed',
  }, // 프로필 선택 / 이름 입력 화면 진입
  MBTI_REPLY_CREATE_QUESTIONNAIRE_VIEWED: {
    ga: 'mbti_reply_create_questionnaire_visit',
    amp: 'Mbti Reply Create - Questionnaire Viewed',
  }, // 질문지 화면 진입
  MBTI_REPLY_CREATE_PRE_QUESTION_VIEWED: {
    ga: 'mbti_reply_create_pre_question_visit',
    amp: 'Mbti Reply Create - Pre Question Viewed',
  }, // pre_question 화면 진입
  MBTI_REPLY_CREATE_SUBMIT: { ga: 'mbti_reply_create_submit', amp: 'Mbti Reply Create - Submit' }, // pre_question 화면에서 ‘작성 완료’ 버튼 클릭

  CLICK: { ga: 'click' },
  ROUTE: { ga: 'route' },
  HTTP: { ga: 'http' },
  CONTENT_CREATED: { ga: 'content_created', amp: 'Content - Created' },
  APP_LINK_CLICKED: { ga: 'app_link_clicked', amp: 'App Link - Clicked' },
  CODE_PUSH: { ga: 'code_push' },
  EXTERNAL_LINK: { ga: 'external_link' },
  APP_UPDATE: { ga: 'app_update' },
  MAIN_FEED_EXPOSE: { ga: 'main_impression' },
  PROMOTION_PUSH: { ga: 'promotion_push' },
  PUSH_PERMISSION_TOGGLE: { ga: 'device_push_toggle', amp: 'Device - Push Permission Toggle' }, // 기기 푸시 on/off
  RESULT_SAVE_BUTTON_CLICKED: { ga: 'result_save_button_clicked', amp: 'Result Save - Button Clicked' }, // 결과 저장하기 버튼 클릭
  RESULT_SAVE_IMPRESSION: { ga: 'result_save_impression', amp: 'Result Save - Impression' }, // 결과 저장하기 노출
  RESULT_SAVE_MODAL_BUTTON_CLICKED: {
    ga: 'result_save_modal_button_clicked',
    amp: 'Result Save Modal - Button Clicked',
  }, // 결과 저장하기 모달 버튼 클릭
  RESULT_SAVE_MODAL_IMPRESSION: { ga: 'result_save_modal_impression', amp: 'Result Save Modal - Impression' }, // 결과 저장하기 모달 노출

  // 성격 유형 검사
  MBTI_TEST_SHARE_CLICKED: { ga: 'click_mbti_test_share', amp: 'Mbti Test - Share Clicked' },
  MBTI_TEST_SUBMIT: { ga: 'mbti_test_submit', amp: 'Mbti Test - Submit' },
  MBTI_TEST_RESULT_VIEWED: { ga: 'mbti_test_result_visit', amp: 'Mbti Test - Result Viewed' },
  MBTI_TEST_QUESTIONNAIRE_VIEWED: { ga: 'mbti_test_questionnaire_visit', amp: 'Mbti Test - Questionnaire Viewed' },
  MBTI_TEST_CREATE_QUIZ_CLICKED: { ga: 'click_quiz_create_from_mbti_test', amp: 'Mbti Test - Create Quiz Clicked' },

  // 성격 유형 퀴즈
  MBTI_QUIZ_GUEST_REPLY_DETAIL_VIEWED: {
    ga: 'mbti_quiz_guest_reply_detail_visit',
    amp: 'Mbti Quiz - Guest Reply Detail - Viewed',
  }, // [성격 유형 퀴즈 > 게스트 응답 디테일] 게스트 응답 상세로 이동
  MBTI_QUIZ_GUEST_REPLY_DETAIL_MAKE_OWN_CLICKED: {
    ga: 'mbti_quiz_guest_reply_detail_make_own',
    amp: 'Mbti Quiz - Guest Reply Detail - Make Own Clicked',
  }, // [성격 유형 퀴즈 > 게스트 응답 디테일] 내 퀴즈 만들기 클릭
  MBTI_QUIZ_GUEST_REPLY_DETAIL_NEXT_CLICKED: {
    ga: 'mbti_quiz_guest_reply_detail_next',
    amp: 'Mbti Quiz - Guest Reply Detail - Next Clicked',
  }, // [성격 유형 퀴즈 > 게스트 응답 디테일] 다음 버튼 클릭
  MBTI_QUIZ_GUEST_REPLY_DETAIL_PREV_CLICKED: {
    ga: 'mbti_quiz_guest_reply_detail_prev',
    amp: 'Mbti Quiz - Guest Reply Detail - Prev Clicked',
  }, // [성격 유형 퀴즈 > 게스트 응답 디테일] 이전 버튼 클릭
  MBTI_QUIZ_HOST_REPLY_DETAIL_NEXT_CLICKED: {
    ga: 'mbti_quiz_host_reply_detail_next',
    amp: 'Mbti Quiz - Host Reply Detail - Next Clicked',
  }, // [성격 유형 퀴즈 > 호스트 ‘응답’ 디테일] 다음 버튼 클릭
  MBTI_QUIZ_HOST_REPLY_DETAIL_PREV_CLICKED: {
    ga: 'mbti_quiz_host_reply_detail_prev',
    amp: 'Mbti Quiz - Host Reply Detail - Prev Clicked',
  }, // [성격 유형 퀴즈 > 호스트 ‘응답’ 디테일] 이전 버튼 클릭
  MBTI_QUIZ_HOST_REPLY_DETAIL_VIEWED: {
    ga: 'mbti_quiz_host_reply_detail_visit',
    amp: 'Mbti Quiz - Host Reply Detail - Viewed',
  }, // [성격 유형 퀴즈 > 호스트 ‘응답’ 디테일] 호스트 응답 상세로 이동
  MBTI_QUIZ_HOST_QUIZ_DETAIL_VIEWED: {
    ga: 'mbti_quiz_host_quiz_detail_visit',
    amp: 'Mbti Quiz - Host Quiz Detail - Viewed',
  }, // [성격 유형 퀴즈 > 호스트 ‘퀴즈’ 디테일] 호스트 퀴즈 상세로 이동
  MBTI_QUIZ_HOST_QUIZ_DETAIL_SAVE_RESULT_CLICKED: {
    ga: 'mbti_quiz_host_quiz_detail_save_result_clicked',
    amp: 'Mbti Quiz - Host Quiz Detail - Save Result Clicked',
  }, // [성격 유형 퀴즈 > 호스트 ‘퀴즈’ 디테일] 비회원 결과 저장 버튼 클릭

  //
  WEB_OPEN_WITH_APP_BUTTON_CLICKED: { ga: 'open_with_app_clicked', amp: 'Web - Open with App Button Clicked' }, // [웹] 타입스 앱에서 열기 버튼 클릭
  // 친구목록
  FRIEND_LIST_CONTACT_PERMISSION_DIALOG_VIEWED: {
    ga: 'contact_permission_dialog_showed',
    amp: 'Friend List - Contact Permission Dialog Viewed',
  }, // [친구 목록 탭] 연락처 권한 동의 Dialog가 나타남
  FRIEND_LIST_CONTACT_PERMISSION_DIALOG_DENY_CLICKED: {
    ga: 'contact_permission_dialog_deny',
    amp: 'Friend List - Contact Permission Dialog Deny Clicked',
  }, // [친구 목록 탭] 연락처 권한 동의 Dialog에서 거절(Deny) 누름
  FRIEND_LIST_CONTACT_PERMISSION_DIALOG_ALLOW_CLICKED: {
    ga: 'contact_permission_dialog_allow',
    amp: 'Friend List - Contact Permission Dialog Allow Clicked',
  }, // [친구 목록 탭] 연락처 권한 동의 Dialog에서 허용(Allow) 누름

  MBTI_REQUIREMENT_MODAL_VIEWED: { ga: 'mbti_requirement_modal_visit', amp: 'Mbti Requirement Modal - Viewed' }, // [성격유형 요청 모달] 모달 표시
  MBTI_REQUIREMENT_MODAL_START_BUTTON_CLICKED: {
    ga: 'click_start_button',
    amp: 'Mbti Requirement Modal - Start Button Clicked',
  }, // [성격유형 요청 모달] ‘성격 유형 퀴즈 시작하기’ 버튼 클릭

  //이상형 MBTI
  IDEAL_TYPE_RESULT_IDEAL_TYPE_BUTTON_CLICKED: {
    amp: 'Ideal Type Result - Ideal Type Button Clicked',
    ga: 'ideal_type_ideal_button_click',
  },
  IDEAL_TYPE_RESULT_MBTI_BUTTON_VIEWED: {
    amp: 'Ideal Type Result - Mbti Button Viewed',
    ga: 'ideal_type_ideal_button_click',
  },
  IDEAL_TYPE_RANK_CLICKED: { amp: 'Ideal Type - Rank Clicked', ga: 'ideal_type_ideal_button_click' },
  IDEAL_TYPE_RESULT_MBTI_BUTTON_CLICKED: {
    amp: 'Ideal Type Result- Mbti Button Clicked',
    ga: 'ideal_type_ideal_button_click',
  },
  IDEAL_TYPE_RESULT_SHARE_BUTTON_CLICKED: {
    amp: 'Ideal Type Result - share Button Clicked',
    ga: 'ideal_type_ideal_button_click',
  },
  IDEAL_TYPE_RESULT_QR_CODE_BUTTON_CLICKED: {
    amp: 'Ideal Type Result - QR Code Button Clicked',
    ga: 'ideal_type_result_qr_code_button_clicked',
  },
  IDEAL_TYPE_RESULT_MBTI_QUIZ_BUTTON_CLICKED: {
    amp: 'Ideal Type Result - Mbti Quiz Button Clicked',
    ga: 'ideal_type_result_mbti_quiz_button_clicked',
  },
  IDEAL_TYPE_RESULT_RESULT_SCREEN_VIEWED: {
    amp: 'Ideal Type Result - Result Screen Viewed',
    ga: 'ideal_type_ideal_button_click',
  },
  IDEAL_TYPE_MATCH_SCREEN_VIEWED: { amp: 'Ideal Type - Match Screen Viewed', ga: 'ideal_type_ideal_button_click' },
  IDEAL_TYPE_QUSTIONNAIRE_SCREEN_VIEWED: {
    amp: 'Ideal Type - questionnaire Screen Viewed',
    ga: 'ideal_type_ideal_button_click',
  },
  IDEAL_TYPE_INTRO_SCREEN_VIEWED: { amp: 'Ideal Type - Intro Screen Viewed', ga: 'ideal_type_ideal_button_click' },
  IDEAL_TYPE_CHEMI_BUTTON_CLICKED: { amp: 'Ideal Type - Chemi Button Clicked', ga: 'ideal_type_ideal_button_click' },
  IDEAL_TYPE_GUEST_INTRO_SCREEN_VIEWED: {
    amp: 'Ideal Type - Guest Intro Screen Viewed',
    ga: 'ideal_type_guest_intro_screen_viewed',
  },
  IDEAL_TYPE_GUEST_MBTI_CHOOSE_SCREEN_VIEWED: {
    amp: 'Ideal Type - Guest Mbti Choose  Screen Viewed',
    ga: 'ideal_type_guest_mbti_choose_screen_viewed',
  },
  IDEAL_TYPE_GUEST_MBTI_BUTTON_CLICKED: {
    amp: 'Ideal Type - Guest Mbti Button Clicked',
    ga: 'ideal_type_guest_mbti_button_click',
  },
  IDEAL_TYPE_GUEST_MBTI_NEEDED_SCREEN_VIEWED: {
    amp: 'Ideal Type - Guest Mbti Needed Screen Viewed',
    ga: 'ideal_type_guest_mbti_needed_screen_viewed',
  },
  IDEAL_TYPE_GUEST_MBTI_CHOSE: { amp: 'Ideal Type - Guest Mbti Chose', ga: 'ideal_type_guest_mbti_chose' },
  HOME_SOCIAL_CONTENT_CLICKED: { amp: 'Home - Social Contents Clicked', ga: 'home_ideal_type_test_click' },
  IDEAL_TYPE_RESULT_VIEW_COMPATIBILITY_BANNER_CLICKED: {
    amp: 'Ideal Type Result - View Compatibility Banner Clicked',
    ga: 'ideal_type_result_view_compatibility_banner_click',
  }, // [이상형 MBTI 결과](웹) 친구와 1:1 궁합보기 배너 클릭
  IDEAL_TYPE_RESULT_HOME_BUTTON_CLICKED: {
    amp: 'Ideal Type Result - Home Button Clicked',
    ga: 'ideal_type_result_home_button_click',
  }, // [이상형 MBTI 결과](웹) 타입스 홈으로 버튼 클릭
  IDEAL_TYPE_GUEST_NEW_MBTI_BUTTON_CLICKED: {
    amp: 'Ideal Type - Guest New Mbti Button Clicked',
    ga: 'ideal_type_guest_new_mbti_button_clicked',
  },
  IDEAL_TYPE_CREATE_SUBMIT: { ga: 'ideal_type_create_submit', amp: 'Ideal Type Create - Submit' }, // 퀴즈 생성
  IDEAL_TYPE_MATCH_IDEAL_TYPE_QUIZ_BUTTON_CLICKED: {
    amp: 'Ideal Type Match - Ideal Type Button Clicked',
    ga: 'ideal_type_match_ideal_type_quiz_button_clicked',
  },
  IDEAL_TYPE_MATCH_FLOATING_BANNER_CLICKED: {
    amp: 'Ideal Type Match - Floating Banner Clicked',
    ga: 'ideal_type_match_floating_banner_clicked',
  },
  IDEAL_TYPE_BACK_TO_RESULT_BUTTON_CLICKED: {
    ga: 'ideal_type_back_to_result_button_clicked',
    amp: 'Ideal Type - Back To Result Button Clicked',
  },
  IDEAL_TYPE_RESULT_DATING_STYLE_BUTTON_CLICKED: {
    amp: 'Ideal Type Result - Dating Style Button Clicked',
    ga: 'ideal_type_result_dating_style_button_clicked',
  },
  IDEAL_TYPE_MATCH_REPLY_MBTI_QUIZ_BUTTON_CLICKED: {
    amp: 'Ideal Type Match Reply - Mbti Quiz Button Clicked',
    ga: 'ideal_type_reply_mbti_quiz_button_clicked',
  },
  IDEAL_TYPE_CHARACTER_CHANGE_BUTTON_CLICKED: {
    ga: 'ideal_type_character_change_button_clicked',
    amp: 'Ideal Type - Character Change Button Clicked',
  },
  IDEAL_TYPE_RESULT_SHOW_ALL_RANKING_BUTTON_CLICKED: {
    ga: 'ideal_type_result_show_all_ranking_button_clicked',
    amp: 'Ideal Type Result - Show All Ranking Button Clicked',
  },
  IDEAL_TYPE_RESULT_MY_MBTI_CHANGE_BUTTON_CLICKED: {
    ga: 'ideal_type_result_my_mbti_change_button_clicked',
    amp: 'Ideal Type Result - My Mbti Change Button Clicked',
  },
  IDEAL_TYPE_RESULT_FLOATING_BANNER_CLICKED: {
    ga: 'ideal_type_result_floating_banner_clicked',
    amp: 'Ideal Type Result - Floating Banner Clicked',
  },

  // 친친 MBTI
  BEST_FRIEND_RESULT_BEST_FRIEND_BUTTON_CLICKED: {
    amp: 'Best Friend Result - Best Friend Button Clicked',
    ga: 'best_friend_result_best_friend_button_clicked',
  },
  BEST_FRIEND_RESULT_MBTI_BUTTON_VIEWED: {
    amp: 'Best Friend Result - Mbti Button Viewed',
    ga: 'best_friend_result_mbti_button_viewed',
  },
  BEST_FRIEND_RANK_CLICKED: { amp: 'Best Friend - Rank Clicked', ga: 'best_friend_rank_clicked' },
  BEST_FRIEND_RESULT_MBTI_BUTTON_CLICKED: {
    amp: 'Best Friend Result- Mbti Button Clicked',
    ga: 'best_friend_result_mbti_button_clicked',
  },
  BEST_FRIEND_RESULT_SHARE_BUTTON_CLICKED: {
    amp: 'Best Friend Result - Share Button Clicked',
    ga: 'best_friend_result_share_button_clicked',
  },
  BEST_FRIEND_RESULT_RESULT_SCREEN_VIEWED: {
    amp: 'Best Friend Result - Result Screen Viewed',
    ga: 'best_friend_result_result_screen_viewed',
  },
  BEST_FRIEND_MATCH_SCREEN_VIEWED: { amp: 'Best Friend - Match Screen Viewed', ga: 'best_friend_match_screen_viewed' },
  BEST_FRIEND_QUSTIONNAIRE_SCREEN_VIEWED: {
    amp: 'Best Friend - Questionnaire Screen Viewed',
    ga: 'best_friend_qustionnaire_screen_viewed',
  },
  BEST_FRIEND_INTRO_SCREEN_VIEWED: { amp: 'Best Friend - Intro Screen Viewed', ga: 'best_friend_intro_screen_viewed' },
  BEST_FRIEND_CHEMI_BUTTON_CLICKED: {
    amp: 'Best Friend - Chemi Button Clicked',
    ga: 'best_friend_chemi_button_clicked',
  },
  BEST_FRIEND_GUEST_INTRO_SCREEN_VIEWED: {
    amp: 'Best Friend - Guest Intro Screen Viewed',
    ga: 'best_friend_guest_intro_screen_viewed',
  },
  BEST_FRIEND_GUEST_MBTI_CHOOSE_SCREEN_VIEWED: {
    amp: 'Best Friend - Guest Mbti Choose Screen Viewed',
    ga: 'best_friend_guest_mbti_choose_screen_viewed',
  },
  BEST_FRIEND_GUEST_MBTI_BUTTON_CLICKED: {
    amp: 'Best Friend - Guest Mbti Button Clicked',
    ga: 'best_friend_guest_mbti_button_click',
  },
  BEST_FRIEND_GUEST_MBTI_NEEDED_SCREEN_VIEWED: {
    amp: 'Best Friend - Guest Mbti Needed Screen Viewed',
    ga: 'best_friend_guest_mbti_needed_screen_viewed',
  },
  BEST_FRIEND_GUEST_MBTI_CHOSE: { amp: 'Best Friend - Guest Mbti Chose', ga: 'best_friend_guest_mbti_chose' },
  HOME_SOCIAL_CONTENT_BEST_FRIEND_CLICKED: { amp: 'Home - Social Contents Clicked', ga: 'home_best_friend_test_click' },
  BEST_FRIEND_RESULT_VIEW_COMPATIBILITY_BANNER_CLICKED: {
    amp: 'Best Friend Result - View Compatibility Banner Clicked',
    ga: 'best_friend_result_view_compatibility_banner_click',
  }, // [친친 MBTI 결과](웹) 친구와 1:1 궁합보기 배너 클릭
  BEST_FRIEND_RESULT_HOME_BUTTON_CLICKED: {
    amp: 'Best Friend Result - Home Button Clicked',
    ga: 'best_friend_result_home_button_click',
  }, // [친친 MBTI 결과](웹) 타입스 홈으로 버튼 클릭
  BEST_FRIEND_GUEST_NEW_MBTI_BUTTON_CLICKED: {
    amp: 'Best Friend - Guest New Mbti Button Clicked',
    ga: 'best_friend_guest_new_mbti_button_clicked',
  },
  BEST_FRIEND_RESULT_CONTENT_BANNER_CLICKED: {
    amp: 'Best Friend Result - Content Banner Clicked',
    ga: 'best_friend_result_content_banner_clicked',
  },

  // 이상형 MBTI 퀴즈 Prefix: IDEAL_TYPE_QUIZ, IDEAL_TYPE_REPLY

  IDEAL_TYPE_REPLY_CREATE_SUBMIT: { ga: 'ideal_type_reply_create_submit', amp: 'Ideal Type Reply Create - Submit' }, // 응답 생성
  IDEAL_TYPE_QUIZ_BACK: { ga: 'ideal_type_quiz_back', amp: 'Ideal Type Quiz - Back' },
  IDEAL_TYPE_QUIZ_FINISH: { ga: 'ideal_type_quiz_finish', amp: 'Ideal Type Quiz - Finish' },
  IDEAL_TYPE_QUIZ_NEXT: { ga: 'ideal_type_quiz_next', amp: 'Ideal Type Quiz - Next' },
  IDEAL_TYPE_QUIZ_REPORT_DETAIL_NEXT_CLICKED: {
    ga: 'ideal_type_quiz_report_detail_next_clicked',
    amp: 'Ideal Type Quiz Report Detail - Next Clicked',
  },
  IDEAL_TYPE_QUIZ_REPORT_DETAIL_PREV_CLICKED: {
    ga: 'ideal_type_quiz_report_detail_prev_clicked',
    amp: 'iIdeal Type Quiz Report Detail - Prev Clicked',
  },
  IDEAL_TYPE_QUIZ_REPORT_DETAIL_SCREEN_VIEWED: {
    ga: 'ideal_type_quiz_report_detail_screen_viewed',
    amp: 'Ideal Type Quiz Report Detail - Screen Viewed',
  },
  IDEAL_TYPE_QUIZ_CREATE_SUBMIT: { ga: 'ideal_type_quiz_create_submit', amp: 'Ideal Type Quiz Create - Submit' }, // 퀴즈 생성
  IDEAL_TYPE_QUIZ_INTRO_SCREEN_VIEWED: {
    amp: 'Ideal Type Quiz - Intro Screen Viewed',
    ga: 'ideal_type_quiz_intro_screen_viewed',
  },
  IDEAL_TYPE_QUIZ_QUESTIONNAIRE_SCREEN_VIEWED: {
    amp: 'Ideal Type Quiz - Questionnaire Screen Viewed',
    ga: 'ideal_type_quiz_questionnaire_screen_viewed',
  },
  IDEAL_TYPE_REPLY_INTRO_SCREEN_VIEWED: {
    amp: 'Ideal Type Reply - Intro Screen Viewed',
    ga: 'ideal_type_reply_intro_screen_viewed',
  },
  IDEAL_TYPE_REPLY_QUESTIONNAIRE_SCREEN_VIEWED: {
    amp: 'Ideal Type Reply - Questionnaire Screen Viewed',
    ga: 'ideal_type_reply_questionnaire_screen_viewed',
  },
  IDEAL_TYPE_QUIZ_REPORT_RANKING_ITEM_CLICKED: {
    amp: 'Ideal Type Quiz Report - Ranking Item Clicked',
    ga: 'ideal_type_quiz_report_rankng_item_clicked',
  },
  IDEAL_TYPE_QUIZ_REPORT_SCREEN_VIEWED: {
    amp: 'Ideal Type Quiz Report - Screen Viewed',
    ga: 'ideal_type_quiz_report_screen_viewed',
  },
  IDEAL_TYPE_QUIZ_REPORT_MBTI_QUIZ_BUTTON_CLICKED: {
    amp: 'Ideal Type Quiz Report - Mbti Quiz Button Clicked',
    ga: 'ideal_type_quiz_report_mbti_quiz_button_clicked',
  },
  IDEAL_TYPE_QUIZ_REPORT_ANSWER_DETAIL_BUTTON_CLICKED: {
    amp: 'Ideal Type Quiz Report - Answer Detail Button Clicked',
    ga: 'ideal_type_quiz_report_answer_detail_button_clicked',
  },
  IDEAL_TYPE_QUIZ_REPORT_DATING_STYLE_BUTTON_CLICKED: {
    amp: 'Ideal Type Quiz Report - Dating Style Button Clicked',
    ga: 'ideal_type_quiz_report_dating_style_button_clicked',
  },
  IDEAL_TYPE_QUIZ_REPORT_SURVEY_ITEM_CLICKED: {
    amp: 'Ideal Type Quiz Report - Survey Item Clicked',
    ga: 'ideal_type_quiz_report_survey_item_clicked',
  },
  IDEAL_TYPE_QUIZ_REPORT_ALL_SURVEY_BUTTON_CLICKED: {
    amp: 'Ideal Type Quiz Report - All Survey Button Clicked',
    ga: 'ideal_type_quiz_report_all_survey_button_clicked',
  },
  IDEAL_TYPE_QUIZ_REPORT_APPSTORE_FLOATING_BUTTON_CLICKED: {
    amp: 'Ideal Type Quiz Report - AppStore Floating Button Clicked',
    ga: 'ideal_type_quiz_report_appstore_floating_button_clicked',
  },
  IDEAL_TYPE_REPLY_REPORT_DETAIL_SCREEN_VIEWED: {
    ga: 'ideal_type_reply_report_detail_screen_viewed',
    amp: 'Ideal Type Reply Report Detail - Screen Viewed',
  },
  IDEAL_TYPE_REPLY_REPORT_SCREEN_VIEWED: {
    amp: 'Ideal Type Reply Report - Screen Viewed',
    ga: 'ideal_type_reply_report_screen_viewed',
  },
  IDEAL_TYPE_REPLY_REPORT_CHEMI_BUTTON_CLICKED: {
    amp: 'Ideal Type Reply Report - Chemi Button Clicked',
    ga: 'ideal_type_reply_report_chemi_button_clicked',
  },
  IDEAL_TYPE_REPLY_REPORT_ANSWER_DETAIL_BUTTON_CLICKED: {
    amp: 'Ideal Type Reply Report - Answer Detail Button Clicked',
    ga: 'ideal_type_reply_report_answer_detail_button_clicked',
  },
  IDEAL_TYPE_REPLY_REPORT_BACK_TO_RANKING_BUTTON_CLICKED: {
    amp: 'Ideal Type Reply Report -  Back To Ranking Button Clicked',
    ga: 'ideal_type_reply_report_back_to_ranking_button_clicked',
  },
  IDEAL_TYPE_REPLY_REPORT_IDEAL_TYPE_QUIZ_BUTTON_CLICKED: {
    amp: 'Ideal Type Reply Report -  Ideal Type Quiz Button Clicked',
    ga: 'ideal_type_reply_report_ideal_type_quiz_button_clicked',
  },
  IDEAL_TYPE_QUIZ_CHARACTER_CHANGE_BUTTON_CLICKED: {
    ga: 'ideal_type_quiz_character_change_button_clicked',
    amp: 'Ideal Type Quiz - Character Change Button_Clicked',
  },
  IDEAL_TYPE_QUIZ_REPORT_MY_MBTI_CHANGE_BUTTON_CLICKED: {
    ga: 'ideal_type_quiz_report_my_mbti_change_button_clicked',
    amp: 'Ideal Type Quiz Report - My Mbti Change Button Clicked',
  },
  IDEAL_TYPE_QUIZ_REPORT_SHOW_ALL_RANKING_BUTTON_CLICKED: {
    ga: 'ideal_type_quiz_report_show_all_ranking_button_clicked',
    amp: 'Ideal Type Quiz Report - Show All Ranking Button Clicked',
  },
  IDEAL_TYPE_QUIZ_REPORT_SHARE_BUTTON_CLICKED: {
    amp: 'Ideal Type Quiz Report - Share Button Clicked',
    ga: 'ideal_type_quiz_report_share_button_clicked',
  },
  IDEAL_TYPE_QUIZ_REPORT_QR_CODE_BUTTON_CLICKED: {
    amp: 'Ideal Type Quiz Report - QR Code Button Clicked',
    ga: 'ideal_type_quiz_report_qr_code_button_clicked',
  },
  IDEAL_TYPE_QUIZ_REPORT_FLOATING_BANNER_CLICKED: {
    ga: 'ideal_type_quiz_report_floating_banner_clicked',
    amp: 'Ideal Type Quiz Report - Floating Banner Clicked',
  },
  IDEAL_TYPE_SELECT_CARD_CLICKED: {
    amp: 'Ideal Type Select - Card Clicked',
    ga: 'ideal_type_select_card_clicked',
  },
  IDEAL_TYPE_SELECT_CREATE_NEW_CLICKED: {
    amp: 'Ideal Type Select - Creat New Clicked',
    ga: 'ideal_type_select_create_new_clicked',
  },
  IDEAL_TYPE_SELECT_VIEWED: {
    amp: 'Ideal Type Select - Viewed',
    ga: 'ideal_type_select_viewed',
  },
  // 성격 유형 선택 모달
  // 성격 유형 선택 모달 진입
  MBTI_SELECT_MODAL_VIEWED: { amp: 'Mbti Select Modal - Viewed', ga: 'mbti_select_modal_viewed' },
  // 성격 유형 선택
  MBTI_SELECT_MODAL_MBTI_CLICKED: { amp: 'Mbti Select Modal - Mbti Clicked', ga: 'mbti_select_modal_mbti_clicked' },
  // 설정 완료 클릭
  MBTI_SELECT_MODAL_SUBMIT_CLICKED: {
    amp: 'Mbti Select Modal - Submit Clicked',
    ga: 'mbti_select_modal_submit_clicked',
  },
  // 추천 컨텐츠 클릭
  RECOMMENDED_CONTENT_CLICKED: { amp: 'Recommended Content - Clicked', ga: 'recommended_content_clicked' },
  RECOMMENDED_CONTENT_HOME_BUTTON_CLICKED: {
    amp: 'Recommended Content - Home Button Clicked',
    ga: 'recommended_content_home_button_clicked',
  },
  RECOMMENDED_CONTENT_LOGIN_MODAL_VIEWED: {
    amp: 'Recommended Content - Login Modal Viewed',
    ga: 'recommended_content_login_modal_viewed',
  },
  RECOMMENDED_CONTENT_LOGIN_MODAL_LOGIN_CLICKED: {
    amp: 'Recommended Content - Login Modal Login Clicked',
    ga: 'recommended_content_login_modal_login_clicked',
  },
  RECOMMENDED_CONTENT_LOGIN_MODAL_INSTALL_CLICKED: {
    amp: 'Recommended Content - Login Modal Install Clicked',
    ga: 'recommended_content_login_modal_install_clicked',
  },
  RECOMMENDED_CONTENT_WEB_MODAL_VIEWED: {
    amp: 'Recommended Content - Web Modal Viewed',
    ga: 'recommended_content_web_modal_viewed',
  },
  RECOMMENDED_CONTENT_WEB_MODAL_WEB_CLICKED: {
    amp: 'Recommended Content - Web Modal Web Clicked',
    ga: 'recommended_content_web_modal_web_clicked',
  },
  RECOMMENDED_CONTENT_WEB_MODAL_INSTALL_CLICKED: {
    amp: 'Recommended Content - Web Modal Install Clicked',
    ga: 'recommended_content_web_modal_install_clicked',
  },

  QR_CODE_MODAL_VIEWED: { amp: 'QR Code Modal - Viewed', ga: 'qr_code_modal_viewed' },
  PROFILE_GUIDE_MODAL_VIEWED: { amp: 'Profile Guide Modal - Viewed', ga: 'profile_guide_modal_viewed' },

  // [쿠팡 광고 모달] 노출
  // [쿠팡 광고 모달] 쿠팡 열기 버튼 클릭
  COUPANG_AD_MODAL_VIEWED: { amp: 'Coupang Ad Modal - Viewed', ga: 'coupang_ad_modal_viewed' },
  COUPANG_AD_MODAL_CLOSE_BUTTON_CLICKED: {
    amp: 'Coupang Ad Modal - Close Button Clicked',
    ga: 'coupang_ad_modal_close_button_clicked',
  },
  COUPANG_AD_MODAL_OPEN_BUTTON_CLICKED: {
    amp: 'Coupang Ad Modal - Open Button Clicked',
    ga: 'coupang_ad_modal_open_button_clicked',
  },

  // [웹] 홈 - 로그 퀴즈 배너 클릭
  HOME_LOG_QUIZ_BANNER_CLICKED: { amp: 'Home - Log Quiz Banner Clicked', ga: 'home_log_quiz_banner_clicked' },
}

export interface LogState {
  sessionId?: number
  path?: string
  userType?: 'host' | 'guest'
}

export enum LogType {
  CLICK = 'click',
  ROUTE = 'route',
  HTTP = 'http',
  CODE_PUSH = 'code_push',
  EXTERNAL_LINK = 'external_link',
  APP_UPDATE = 'app_update',
  MAIN_FEED_EXPOSE = 'main_impression',
  PROMOTION_PUSH = 'promotion_push',
  PUSH_PERMISSION_TOGGLE = 'device_push_toggle', // 기기 푸시 on/off
}

export enum SystemNavigateReason {
  ACCESS_DENIED = 'access denied',
  DELETED_QUIZ_REPORT = 'deleted quiz report',
  ALREADY_ANSWERED = 'already answered',
  ERROR = 'error occurred',
  GO_THROUGH_ROUTE = 'go through this route',
}

export interface RouterLoggingParams extends Params {
  reason?: SystemNavigateReason
  direction?: 'forward' | 'backward'
  from?: string
  to?: string
  navigateType?: NavigateType
}

export interface ClickLoggingParams extends Params {
  elementName: string
  clickElemClassName: string
  clickElemPath: string
}

export interface HttpLoggingParams extends Params {
  method: 'post' | 'patch' | 'get' | 'delete'
  callers: string[]
  httpRequestOptions: HttpRequestOptions
  body?: Params
  apiUrl: string
  statusCode?: number
  errorMessage?: string
}

export interface ExternalLinkLoggingParams extends Params {
  url: string
}

export interface AppUpdateLoggingParams extends Params {
  status: AppUpdateStatus
  clickedButton: 'update' | 'cancel'
}
